import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BrnService, BrnWidgetClientWithMeta } from '../../services/brn.service';
import { firstValueFrom, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-chat-clients',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './chat-clients.component.html',
  styleUrls: ['./chat-clients.component.scss']
})
export class ChatClientsComponent implements OnInit, OnDestroy {
  clients: BrnWidgetClientWithMeta[] = [];
  isLoading = true;
  error: string | null = null;
  botId: string | null = null;
  searchControl = new FormControl<string>('', { nonNullable: true });
  private searchSub?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly brnService: BrnService
  ) {}

  async ngOnInit(): Promise<void> {
    this.botId = this.route.snapshot.paramMap.get('bot_id');
    
    if (!this.botId) {
      this.error = 'No bot_id found in route parameters';
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  private setupSearch(): void {
    if (!this.botId) return;

    this.searchSub = this.searchControl.valueChanges.pipe(
      startWith(this.searchControl.value ?? ''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((name: string) => {
        this.error = null;
        this.isLoading = true;
        const trimmed = name?.trim() ?? '';
        return this.brnService.getWidgetClients(this.botId!, trimmed || undefined).pipe(
          catchError(err => {
            console.error('Failed to load clients', err);
            this.error = 'Failed to load conversations';
            return of([] as BrnWidgetClientWithMeta[]);
          })
        );
      })
    ).subscribe((clients: BrnWidgetClientWithMeta[]) => {
      // Sort by last message timestamp (most recent first)
      clients.sort((a, b) => {
        const timeA = a.metadata?.last_message_at ? new Date(a.metadata.last_message_at).getTime() : 0;
        const timeB = b.metadata?.last_message_at ? new Date(b.metadata.last_message_at).getTime() : 0;
        return timeB - timeA;
      });
      this.clients = clients;
      this.isLoading = false;
    });
  }

  async loadClients(): Promise<void> {
    if (!this.botId) return;

    try {
      this.isLoading = true;
      this.error = null;
      this.clients = await firstValueFrom(this.brnService.getWidgetClients(this.botId));
      
      // Sort by last message timestamp (most recent first)
      this.clients.sort((a, b) => {
        const timeA = a.metadata?.last_message_at ? new Date(a.metadata.last_message_at).getTime() : 0;
        const timeB = b.metadata?.last_message_at ? new Date(b.metadata.last_message_at).getTime() : 0;
        return timeB - timeA;
      });
    } catch (err) {
      console.error('Failed to load clients', err);
      this.error = 'Failed to load conversations';
    } finally {
      this.isLoading = false;
    }
  }

  navigateToChat(client: BrnWidgetClientWithMeta): void {
    if (!this.botId) {
      console.error('No botId available');
      return;
    }
    
    const userId = this.getUserId(client);
    console.log('Navigating to chat:', { botId: this.botId, userId, client });
    
    if (userId) {
      this.router.navigate(['/chat', this.botId, userId]);
    } else {
      console.error('No userId found in client:', client);
    }
  }

  getUserId(client: BrnWidgetClientWithMeta): string | null {
    // Cast client to any to access the data property
    const clientAny = client as any;
    
    // Check if data.user_id exists directly on client
    if (clientAny?.data?.user_id) {
      console.log('getUserId (from client.data.user_id):', clientAny.data.user_id);
      return clientAny.data.user_id;
    }
    
    // Try provider_data.data.user_id
    const providerData = client.provider_data as Record<string, unknown>;
    if (providerData?.['data']) {
      const data = providerData['data'] as Record<string, unknown>;
      const userId = data?.['user_id'] as string;
      if (userId) {
        console.log('getUserId (from provider_data.data.user_id):', userId);
        return userId;
      }
    }
    
    // Fallback to provider_data.user_id
    const userId = (providerData?.['user_id'] as string) || null;
    console.log('getUserId (fallback):', { provider_data: providerData, userId });
    return userId;
  }

  getClientDisplayName(client: BrnWidgetClientWithMeta): string {
    if (client.name && client.name.trim()) {
      return client.name;
    }
    const userId = this.getUserId(client);
    return userId || client.client_id;
  }

  getClientInitials(client: BrnWidgetClientWithMeta): string {
    const name = this.getClientDisplayName(client);
    const words = name.trim().split(/\s+/);
    
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    
    return name.substring(0, 2).toUpperCase();
  }

  getLastMessagePreview(client: BrnWidgetClientWithMeta): string {
    // Check for last_message_created field
    const clientAny = client as any;
    if (clientAny.last_message_created) {
      const timeAgo = this.formatTimeAgo(clientAny.last_message_created);
      return `Last message ${timeAgo}`;
    }
    
    // Fallback to preview if available
    const preview = client.metadata?.last_message_preview;
    if (!preview) return 'No messages yet';
    
    const maxLength = 60;
    if (preview.length > maxLength) {
      return preview.substring(0, maxLength) + '...';
    }
    return preview;
  }

  private formatTimeAgo(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  formatLastMessageTime(client: BrnWidgetClientWithMeta): string {
    const timestamp = client.metadata?.last_message_at;
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  trackByClientId(index: number, client: BrnWidgetClientWithMeta): string {
    return client.client_id;
  }

  getSubtitle(): string {
    const count = this.clients.length;
    if (count === 0) {
      return 'You have no previous chats with Lilo';
    }
    if (count === 1) {
      return 'You have 1 previous chat with Lilo';
    }
    return `You have ${count} previous chats with Lilo`;
  }
}
